import { useAtom, useSetAtom } from 'jotai'
import { useCallback, useMemo } from 'react'
import { sidebarOpenAtom, isDarkAtom } from './store'
import { useSessions } from '../hooks/useSessions'
import { saveTheme } from '../lib/storage'
import type { ActionHandler } from '@json-render/core'

/**
 * Returns action handlers for the JSONUIProvider.
 * Bridges json-render actions → Jotai state updates + session management.
 */
export function useChatbotActions() {
  const { createSession, selectSession, deleteSession } = useSessions()
  const [sidebarOpen, setSidebarOpen] = useAtom(sidebarOpenAtom)
  const setIsDark = useSetAtom(isDarkAtom)

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev)
  }, [setSidebarOpen])

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev
      document.documentElement.classList.toggle('dark', next)
      saveTheme(next ? 'dark' : 'light')
      return next
    })
  }, [setIsDark])

  const handlers = useMemo<Record<string, ActionHandler>>(
    () => ({
      newSession: () => { createSession() },
      selectSession: (params: Record<string, unknown>) => {
        selectSession(params.sessionId as string)
      },
      deleteSession: (params: Record<string, unknown>) => {
        deleteSession(params.sessionId as string)
      },
      toggleSidebar: () => { toggleSidebar() },
      toggleTheme: () => { toggleTheme() },
      // sendMessage is intentionally a no-op here. Input submission is wired
      // imperatively via chatInputActionsRef in useChat.ts to avoid routing
      // through json-render's action dispatch (which would require an extra
      // round-trip through the provider tree for a latency-sensitive path).
      // The catalog entry exists purely for schema documentation purposes.
      sendMessage: () => {},
    }),
    [createSession, selectSession, deleteSession, toggleSidebar, toggleTheme],
  )

  return { handlers, sidebarOpen }
}
